import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { YshPanelComponent } from '../../components/ysh-panel/ysh-panel.component';
import { YshPageHeaderComponent } from '../../components/ysh-page-header/ysh-page-header.component';
import { Trait, YshTraitsComponent } from '../../components/ysh-traits/ysh-traits.component';
import { GalleryImage, YshGalleryComponent } from '../../components/ysh-gallery/ysh-gallery.component';
import { YshInspectionReportComponent } from '../../components/ysh-inspection-report/ysh-inspection-report.component';
import { Order } from '../../models/order';
import { OrderService } from '../../services/api/order/order.services';
import { AppRoutes, companyRoute } from '../../app.routes';
import { CompanyService } from '../../services/api/company/company.service';
import { first } from 'rxjs';

// Answers that map to a colored status badge in YshBadgeComponent. Anything
// else (e.g. numeric measurements) is shown as a plain value.
const STATUS_ANSWERS = ['ok', 'pass', 'fail', 'needs repair', 'complete', 'pending'];

@Component({
    selector: 'app-inspection',
    templateUrl: './inspection.component.html',
    styleUrl: './inspection.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        YshPageHeaderComponent,
        YshPanelComponent,
        YshTraitsComponent,
        YshGalleryComponent,
        YshInspectionReportComponent,
    ]
})
export class InspectionComponent {
  @Input() uid: string;

  order: Order;
  orderOverview: Trait[];
  orderDetails: Trait[];
  orderPhotos: GalleryImage[] = [];
  orderVehicleRoute: string;
  backLinkRoute: string;

  constructor(private orderService: OrderService, private companyService: CompanyService) {}

  ngOnInit(): void {
    this.getOrder();
    this.getCompany();
  }

  getCompany() {
    this.companyService.currentCompany$.pipe(first()).subscribe(company => {
      this.backLinkRoute = '/' + companyRoute(AppRoutes.Inspections, company);
    });
  }
  
  getOrder() {
    this.orderService.get(this.uid).subscribe(order => {
      this.order = order;
      this.orderOverview = [
        { 
          label: 'Description', 
          value: this.order?.service?.name || 'Unknown',
        },
        {
          label: 'Vehicle', 
          value: this.order?.vehicle?.description || 'Unknown',
          linkHref: `/${AppRoutes.Vehicles}/${this.order.vehicle.uid}` 
        },
        { 
          label: 'VIN', 
          value: this.order?.vehicle?.vin || 'Unknown',
        },
        { 
          label: 'Completed', 
          value: this.order?.timeCompletedString || 'Unknown',
        },
        { 
          label: 'Inspector', 
          value: this.order?.inspection?.inspectorName || 'Unknown',
        },
        { 
          label: 'Inspection Result', 
          value: this.order?.inspection?.result || 'None', 
          badgeTheme: 'status',
        },
        { 
          label: 'Inspection Form', 
          value: this.order?.inspection?.documentUrl ? 'Download PDF' : 'None', 
          linkHref: this.order?.inspection?.documentUrl,
          linkIcon: 'description',
        },
      ];      

      this.orderDetails = this.order?.inspection?.details?.map(detail => {
        // Only pass/fail-style answers get a status badge; measurements
        // (e.g. tire depth) render as plain values.
        const isStatus = typeof detail.answer === 'string'
          && STATUS_ANSWERS.includes(detail.answer.toLowerCase());
        return {
          label: detail.question,
          value: detail.answer ?? 'None',
          badgeTheme: isStatus ? 'status' : undefined,
        };
      });
      
      this.orderPhotos = this.order?.inspection?.photos?.map(photo => ({
        src: photo.url,
        // TODO: Fix getters not working for this
        caption: photo.label?.replace(/_/g,' ') || 'Inspection Photo',
      }));
    });
  }
}
